import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Plus, Minus, Trash2, ShoppingCart, CreditCard, 
  Banknote, QrCode, User, Phone, X, Receipt, Send,
  ChevronRight, Package, LogOut, Loader2, Printer, Check, ClipboardList
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function POSBilling() {
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  
  // Auth state
  const [manager, setManager] = useState(null);
  const [token, setToken] = useState(null);
  
  // Products state
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(false);
  
  // Cart state
  const [cart, setCart] = useState([]);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  
  // Customer state
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerName, setCustomerName] = useState('');
  
  // Payment state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrData, setQRData] = useState(null);
  const [processingOrder, setProcessingOrder] = useState(false);
  const [sendWhatsApp, setSendWhatsApp] = useState(true);
  
  // Receipt state
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [completedOrder, setCompletedOrder] = useState(null);
  
  // Settings
  const [settings, setSettings] = useState(null);

  // Pending POs to receive (GRN badge)
  const [pendingPOCount, setPendingPOCount] = useState(0);

  // Check auth on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('pos_token');
    const storedManager = localStorage.getItem('pos_manager');
    
    if (!storedToken || !storedManager) {
      navigate('/pos/login');
      return;
    }
    
    setToken(storedToken);
    setManager(JSON.parse(storedManager));
    fetchCategories(storedToken);
    fetchSettings(storedToken);
    fetchPendingPOs(storedToken);
  }, [navigate]);

  const fetchPendingPOs = async (authToken) => {
    try {
      const r = await fetch(`${API_URL}/api/pos/purchase-orders`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (r.ok) {
        const data = await r.json();
        setPendingPOCount(Array.isArray(data) ? data.length : 0);
      }
    } catch (e) {
      console.error('Pending POs fetch failed:', e);
    }
  };

  // Fetch products when search or category changes
  useEffect(() => {
    if (token) {
      fetchProducts();
    }
  }, [searchQuery, selectedCategory, token]);

  // Focus search on mount
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  const fetchCategories = async (authToken) => {
    try {
      const response = await fetch(`${API_URL}/api/pos/categories`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSettings = async (authToken) => {
    try {
      const response = await fetch(`${API_URL}/api/pos/settings`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const data = await response.json();
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchProducts = useCallback(async () => {
    if (!token) return;
    
    setLoadingProducts(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory) params.append('category_id', selectedCategory);
      
      const response = await fetch(`${API_URL}/api/pos/products?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      toast.error('Error fetching products');
    } finally {
      setLoadingProducts(false);
    }
  }, [token, searchQuery, selectedCategory]);

  // Cart functions
  const addToCart = (product) => {
    if (product.stock <= 0) {
      toast.error('Product out of stock');
      return;
    }
    
    const existingItem = cart.find(item => item.product_id === product.id);
    
    if (existingItem) {
      if (existingItem.quantity >= product.stock) {
        toast.error('Not enough stock');
        return;
      }
      updateCartQuantity(product.id, existingItem.quantity + 1);
    } else {
      const gstRate = product.gst_rate || settings?.default_gst_rate || 0;
      const gstAmount = (product.price * gstRate) / 100;
      
      setCart([...cart, {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        unit_price: product.price,
        gst_rate: gstRate,
        gst_amount: gstAmount,
        discount_amount: 0,
        total: product.price + gstAmount,
        stock: product.stock,
        image: product.image
      }]);
    }
  };

  const updateCartQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        if (newQuantity > item.stock) {
          toast.error('Not enough stock');
          return item;
        }
        const subtotal = item.unit_price * newQuantity;
        const gstAmount = (subtotal * item.gst_rate) / 100;
        return {
          ...item,
          quantity: newQuantity,
          gst_amount: gstAmount,
          total: subtotal + gstAmount - item.discount_amount
        };
      }
      return item;
    }));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const clearCart = () => {
    setCart([]);
    setAppliedDiscount(null);
    setDiscountCode('');
    setCustomerPhone('');
    setCustomerName('');
  };

  // Calculate totals
  const subtotal = cart.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
  const totalGST = cart.reduce((sum, item) => sum + item.gst_amount, 0);
  const discountAmount = appliedDiscount?.calculated_discount || 0;
  const grandTotal = subtotal + totalGST - discountAmount;

  // Apply discount
  const applyDiscount = async () => {
    if (!discountCode) return;
    
    try {
      const response = await fetch(`${API_URL}/api/pos/discounts/validate?code=${discountCode}&order_amount=${subtotal}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }
      
      const data = await response.json();
      setAppliedDiscount(data);
      toast.success(`Discount applied: ₹${data.calculated_discount.toFixed(2)}`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Generate UPI QR
  const generateQR = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/pos/generate-upi-qr?amount=${grandTotal}&order_ref=POS-${Date.now()}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      setQRData(data);
      setShowQRModal(true);
    } catch (error) {
      toast.error('Error generating QR code');
    }
  };

  // Process payment
  const processPayment = async (method) => {
    if (!customerPhone) {
      toast.error('Please enter customer phone number');
      return;
    }
    
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    
    setPaymentMethod(method);
    
    if (method === 'UPI') {
      await generateQR();
    } else {
      setShowPaymentModal(true);
    }
  };

  // Complete order
  const completeOrder = async () => {
    setProcessingOrder(true);
    
    try {
      const orderData = {
        items: cart.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          gst_rate: item.gst_rate,
          gst_amount: item.gst_amount,
          discount_amount: item.discount_amount,
          total: item.total
        })),
        customer_phone: customerPhone,
        customer_name: customerName || null,
        payment_method: paymentMethod,
        subtotal: subtotal,
        total_gst: totalGST,
        total_discount: discountAmount,
        grand_total: grandTotal,
        payment_reference: paymentReference || null,
        send_whatsapp: sendWhatsApp
      };
      
      const response = await fetch(`${API_URL}/api/pos/orders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }
      
      const data = await response.json();
      
      // Store completed order data for receipt
      setCompletedOrder({
        order_number: data.order_number,
        items: orderData.items,
        customer_phone: customerPhone,
        customer_name: customerName,
        payment_method: paymentMethod,
        subtotal: subtotal,
        total_gst: totalGST,
        total_discount: discountAmount,
        grand_total: grandTotal,
        payment_reference: paymentReference,
        whatsapp_sent: data.whatsapp_sent,
        created_at: new Date().toISOString()
      });
      
      // Show receipt modal
      setShowReceiptModal(true);
      setShowPaymentModal(false);
      setShowQRModal(false);
      
      toast.success(`Order #${data.order_number} created!`);
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessingOrder(false);
    }
  };
  
  // Close receipt and reset
  const closeReceipt = () => {
    setShowReceiptModal(false);
    setCompletedOrder(null);
    clearCart();
    setPaymentReference('');
  };
  
  // Print receipt
  const printReceipt = () => {
    const printContent = document.getElementById('receipt-content');
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Receipt - ${completedOrder?.order_number}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Courier New', monospace; 
              padding: 10px;
              width: 80mm;
              font-size: 12px;
            }
            .header { text-align: center; margin-bottom: 10px; border-bottom: 1px dashed #000; padding-bottom: 10px; }
            .header h1 { font-size: 18px; margin-bottom: 5px; }
            .header p { font-size: 10px; }
            .info { margin: 10px 0; }
            .info p { margin: 3px 0; }
            .items { margin: 10px 0; border-top: 1px dashed #000; border-bottom: 1px dashed #000; padding: 10px 0; }
            .item { display: flex; justify-content: space-between; margin: 5px 0; }
            .item-name { flex: 1; }
            .item-qty { width: 40px; text-align: center; }
            .item-price { width: 70px; text-align: right; }
            .totals { margin: 10px 0; }
            .total-row { display: flex; justify-content: space-between; margin: 3px 0; }
            .grand-total { font-size: 16px; font-weight: bold; border-top: 1px dashed #000; padding-top: 10px; margin-top: 10px; }
            .footer { text-align: center; margin-top: 15px; font-size: 10px; border-top: 1px dashed #000; padding-top: 10px; }
            @media print { body { width: 80mm; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Logout
  const handleLogout = () => {
    localStorage.removeItem('pos_token');
    localStorage.removeItem('pos_manager');
    navigate('/pos/login');
  };

  if (!manager) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-100" data-testid="pos-billing-screen">
      {/* Top Header */}
      <header className="bg-emerald-700 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
            <Package className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h1 className="font-bold text-lg">NammaHut POS</h1>
            <p className="text-emerald-200 text-xs">{manager.store_location || 'Main Store'}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/pos/grn')}
            data-testid="grn-badge-btn"
            className="relative flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 transition-colors"
            title="Receive stock (GRN)"
          >
            <ClipboardList className="w-5 h-5" />
            <span className="hidden sm:inline text-sm">Receive Stock</span>
            {pendingPOCount > 0 && (
              <span
                data-testid="grn-badge-count"
                className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 bg-amber-400 text-slate-900 text-xs font-bold rounded-full flex items-center justify-center"
              >
                {pendingPOCount}
              </span>
            )}
          </button>
          <div className="text-right hidden sm:block">
            <p className="text-sm font-medium">{manager.name}</p>
            <p className="text-xs text-emerald-200">{manager.email}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            className="text-white hover:bg-emerald-600"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Products */}
        <div className="flex-1 flex flex-col p-4 overflow-hidden">
          {/* Search & Filter */}
          <div className="flex gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                ref={searchInputRef}
                placeholder="Search products or scan barcode..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 bg-white"
                data-testid="pos-search-input"
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="h-12 px-4 rounded-lg border bg-white min-w-[150px]"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* Products Grid */}
          <div className="flex-1 overflow-y-auto">
            {loadingProducts ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
              </div>
            ) : products.length === 0 ? (
              <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 p-10 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-amber-50 flex items-center justify-center">
                  <Package className="w-8 h-8 text-amber-500" />
                </div>
                {searchQuery || selectedCategory ? (
                  <>
                    <h3 className="font-semibold text-gray-900 mb-1">No matching products</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Try a different search or clear filters.
                    </p>
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedCategory('');
                      }}
                      className="text-emerald-700 font-medium text-sm hover:underline"
                    >
                      Clear filters
                    </button>
                  </>
                ) : (
                  <>
                    <h3 className="font-semibold text-gray-900 mb-1">No POS products yet</h3>
                    <p className="text-sm text-gray-500 mb-4 max-w-sm mx-auto">
                      Your store manager can only sell products that admin has enabled for POS.
                      Ask admin to add products via the POS Admin panel.
                    </p>
                    <a
                      href="/pos/admin/products"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium"
                      data-testid="empty-goto-admin"
                    >
                      Open POS Admin
                      <ChevronRight className="w-4 h-4" />
                    </a>
                  </>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {products.map(product => (
                  <button
                    key={product.id}
                    onClick={() => addToCart(product)}
                    disabled={product.stock <= 0}
                    className={`bg-white rounded-xl p-3 text-left transition-all hover:shadow-lg hover:scale-[1.02] ${
                      product.stock <= 0 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    data-testid={`product-${product.id}`}
                  >
                    <div className="aspect-square rounded-lg bg-gray-100 mb-2 overflow-hidden">
                      {product.image ? (
                        <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-8 h-8 text-gray-300" />
                        </div>
                      )}
                    </div>
                    <h3 className="font-medium text-sm text-gray-900 line-clamp-2 mb-1">
                      {product.name}
                    </h3>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-emerald-600">₹{product.price}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        product.stock <= 5 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {product.stock} left
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Cart */}
        <div className="w-96 bg-white border-l flex flex-col">
          {/* Customer Info */}
          <div className="p-4 border-b bg-gray-50">
            <div className="flex gap-2 mb-2">
              <div className="relative flex-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Customer Phone *"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  className="pl-9 h-10"
                  data-testid="customer-phone-input"
                />
              </div>
            </div>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Customer Name (optional)"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="pl-9 h-10"
              />
            </div>
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-4">
            {cart.length === 0 ? (
              <div className="text-center text-gray-400 py-10">
                <ShoppingCart className="w-12 h-12 mx-auto mb-3" />
                <p>Cart is empty</p>
                <p className="text-sm">Add products to start billing</p>
              </div>
            ) : (
              <div className="space-y-3">
                {cart.map(item => (
                  <div key={item.product_id} className="flex gap-3 bg-gray-50 rounded-lg p-3">
                    <div className="w-12 h-12 rounded-lg bg-gray-200 overflow-hidden flex-shrink-0">
                      {item.image ? (
                        <img src={item.image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-5 h-5 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm text-gray-900 truncate">{item.product_name}</h4>
                      <p className="text-xs text-gray-500">₹{item.unit_price} × {item.quantity}</p>
                      <p className="font-semibold text-emerald-600">₹{item.total.toFixed(2)}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <button
                        onClick={() => removeFromCart(item.product_id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <div className="flex items-center gap-1 bg-white rounded-lg border">
                        <button
                          onClick={() => updateCartQuantity(item.product_id, item.quantity - 1)}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                        <button
                          onClick={() => updateCartQuantity(item.product_id, item.quantity + 1)}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Discount Code */}
          {cart.length > 0 && (
            <div className="px-4 pb-3">
              <div className="flex gap-2">
                <Input
                  placeholder="Discount code"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                  className="h-10"
                />
                <Button onClick={applyDiscount} variant="outline" className="h-10">
                  Apply
                </Button>
              </div>
              {appliedDiscount && (
                <div className="mt-2 flex items-center justify-between text-sm bg-emerald-50 text-emerald-700 px-3 py-2 rounded-lg">
                  <span>{appliedDiscount.discount_name}</span>
                  <button onClick={() => setAppliedDiscount(null)} className="hover:text-emerald-900">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Totals */}
          <div className="p-4 border-t bg-gray-50 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Subtotal</span>
              <span>₹{subtotal.toFixed(2)}</span>
            </div>
            {totalGST > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">GST</span>
                <span>₹{totalGST.toFixed(2)}</span>
              </div>
            )}
            {discountAmount > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Discount</span>
                <span>-₹{discountAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold pt-2 border-t">
              <span>Total</span>
              <span className="text-emerald-600">₹{grandTotal.toFixed(2)}</span>
            </div>
          </div>

          {/* WhatsApp Toggle */}
          <div className="px-4 py-2 border-t">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={sendWhatsApp}
                onChange={(e) => setSendWhatsApp(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              <Send className="w-4 h-4 text-green-600" />
              <span className="text-sm">Send bill via WhatsApp</span>
            </label>
          </div>

          {/* Payment Buttons */}
          <div className="p-4 border-t space-y-2">
            <div className="grid grid-cols-3 gap-2">
              <Button
                onClick={() => processPayment('CASH')}
                disabled={cart.length === 0}
                className="h-14 flex-col gap-1 bg-emerald-600 hover:bg-emerald-700"
                data-testid="pay-cash-btn"
              >
                <Banknote className="w-5 h-5" />
                <span className="text-xs">Cash</span>
              </Button>
              <Button
                onClick={() => processPayment('UPI')}
                disabled={cart.length === 0}
                className="h-14 flex-col gap-1 bg-violet-600 hover:bg-violet-700"
                data-testid="pay-upi-btn"
              >
                <QrCode className="w-5 h-5" />
                <span className="text-xs">UPI</span>
              </Button>
              <Button
                onClick={() => processPayment('CARD')}
                disabled={cart.length === 0}
                className="h-14 flex-col gap-1 bg-blue-600 hover:bg-blue-700"
                data-testid="pay-card-btn"
              >
                <CreditCard className="w-5 h-5" />
                <span className="text-xs">Card</span>
              </Button>
            </div>
            <Button
              onClick={clearCart}
              variant="outline"
              className="w-full"
              disabled={cart.length === 0}
            >
              Clear Cart
            </Button>
          </div>
        </div>
      </div>

      {/* UPI QR Modal */}
      {showQRModal && qrData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6">
            <div className="text-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Scan to Pay</h3>
              <p className="text-3xl font-bold text-emerald-600 mt-2">₹{grandTotal.toFixed(2)}</p>
            </div>
            
            <div className="flex justify-center mb-4">
              <img src={qrData.qr_code} alt="UPI QR Code" className="w-64 h-64" />
            </div>
            
            <p className="text-center text-sm text-gray-500 mb-4">
              UPI ID: {qrData.upi_vpa}
            </p>
            
            <div className="space-y-2">
              <Input
                placeholder="Enter UPI Transaction Reference"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
              />
              <Button
                onClick={completeOrder}
                disabled={processingOrder}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                {processingOrder ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Payment Received
                  </>
                )}
              </Button>
              <Button
                onClick={() => setShowQRModal(false)}
                variant="outline"
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cash/Card Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                {paymentMethod === 'CASH' ? (
                  <Banknote className="w-8 h-8 text-emerald-600" />
                ) : (
                  <CreditCard className="w-8 h-8 text-emerald-600" />
                )}
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                {paymentMethod === 'CASH' ? 'Cash Payment' : 'Card Payment'}
              </h3>
              <p className="text-3xl font-bold text-emerald-600 mt-2">₹{grandTotal.toFixed(2)}</p>
            </div>
            
            {paymentMethod === 'CARD' && (
              <Input
                placeholder="Card Transaction Reference (optional)"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                className="mb-4"
              />
            )}
            
            <div className="space-y-2">
              <Button
                onClick={completeOrder}
                disabled={processingOrder}
                className="w-full bg-emerald-600 hover:bg-emerald-700 h-12"
              >
                {processingOrder ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Receipt className="w-5 h-5 mr-2" />
                    Complete Order
                  </>
                )}
              </Button>
              <Button
                onClick={() => setShowPaymentModal(false)}
                variant="outline"
                className="w-full h-12"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {showReceiptModal && completedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Receipt Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-emerald-700">Order Complete!</h3>
              <button onClick={closeReceipt} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Printable Receipt Content */}
            <div id="receipt-content" className="p-6">
              {/* Store Header */}
              <div className="header text-center mb-4 pb-4 border-b-2 border-dashed border-gray-300">
                <h1 className="text-xl font-bold text-gray-900">
                  {settings?.receipt_header || 'NammaHut'}
                </h1>
                <p className="text-sm text-gray-500">Traditional Marketplace</p>
                <p className="text-xs text-gray-400 mt-1">GST: XXXXXXXXXX</p>
              </div>
              
              {/* Order Info */}
              <div className="info mb-4 text-sm">
                <div className="flex justify-between mb-1">
                  <span className="text-gray-500">Order #:</span>
                  <span className="font-bold">{completedOrder.order_number}</span>
                </div>
                <div className="flex justify-between mb-1">
                  <span className="text-gray-500">Date:</span>
                  <span>{new Date(completedOrder.created_at).toLocaleString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</span>
                </div>
                <div className="flex justify-between mb-1">
                  <span className="text-gray-500">Customer:</span>
                  <span>{completedOrder.customer_name || completedOrder.customer_phone}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Payment:</span>
                  <span className={`font-medium ${
                    completedOrder.payment_method === 'CASH' ? 'text-emerald-600' :
                    completedOrder.payment_method === 'UPI' ? 'text-violet-600' : 'text-blue-600'
                  }`}>{completedOrder.payment_method}</span>
                </div>
              </div>
              
              {/* Items */}
              <div className="items py-4 border-y-2 border-dashed border-gray-300">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-500 text-xs">
                      <th className="text-left pb-2">Item</th>
                      <th className="text-center pb-2 w-12">Qty</th>
                      <th className="text-right pb-2 w-20">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completedOrder.items.map((item, idx) => (
                      <tr key={item.product_id || `${item.product_name}-${idx}`} className="border-b border-gray-100">
                        <td className="py-2 pr-2">
                          <span className="line-clamp-2">{item.product_name}</span>
                          <span className="text-xs text-gray-400 block">@ ₹{item.unit_price}</span>
                        </td>
                        <td className="text-center py-2">{item.quantity}</td>
                        <td className="text-right py-2 font-medium">₹{item.total.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Totals */}
              <div className="totals py-4 text-sm">
                <div className="flex justify-between mb-1">
                  <span className="text-gray-500">Subtotal</span>
                  <span>₹{completedOrder.subtotal.toFixed(2)}</span>
                </div>
                {completedOrder.total_gst > 0 && (
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-500">GST</span>
                    <span>₹{completedOrder.total_gst.toFixed(2)}</span>
                  </div>
                )}
                {completedOrder.total_discount > 0 && (
                  <div className="flex justify-between mb-1 text-green-600">
                    <span>Discount</span>
                    <span>-₹{completedOrder.total_discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-3 mt-3 border-t-2 border-dashed border-gray-300 text-lg font-bold">
                  <span>TOTAL</span>
                  <span className="text-emerald-600">₹{completedOrder.grand_total.toFixed(2)}</span>
                </div>
              </div>
              
              {/* Footer */}
              <div className="footer text-center pt-4 border-t-2 border-dashed border-gray-300">
                <p className="text-sm text-gray-600">
                  {settings?.receipt_footer || 'Thank you for shopping with us!'}
                </p>
                <p className="text-xs text-gray-400 mt-2">Visit again at NammaHut</p>
                {completedOrder.whatsapp_sent && (
                  <p className="text-xs text-green-600 mt-2 flex items-center justify-center gap-1">
                    <Send className="w-3 h-3" /> Bill sent via WhatsApp
                  </p>
                )}
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="sticky bottom-0 bg-white border-t p-4 flex gap-3">
              <Button
                onClick={printReceipt}
                variant="outline"
                className="flex-1 h-12"
              >
                <Printer className="w-5 h-5 mr-2" />
                Print Receipt
              </Button>
              <Button
                onClick={closeReceipt}
                className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700"
              >
                <Check className="w-5 h-5 mr-2" />
                New Order
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
